type LsegQuoteRow = Record<string, string | undefined>;

type LsegTimeseriesRow = Record<string, string | number | undefined>;

type LsegRestPayload = {
  data?: LsegQuoteRow[] | LsegTimeseriesRow[];
  metadata?: Record<string, { type?: string }>;
  substatus?: string;
};

type LsegRestGetResponse = {
  data?: LsegRestPayload;
};

type LsegDoryRest = {
  get?: (path: string, ...args: unknown[]) => Promise<LsegRestGetResponse>;
  __ovMainIndexEciSupportApplied?: boolean;
};

type LsegDoryApi = {
  Rest?: LsegDoryRest;
};

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const ECI_QUOTE_EXTRA_FIDS = [
  'q.ECI_ACT_DT',
  'q.VALUE_DT1',
  'q._HSTCLSDATE',
  'q.ECON_PRIOR',
  'q.ECON_ACT',
  'q.ACT_VAL_NS',
  'q.CF_LAST',
] as const;

const ECI_REPORT_DATE_FIELDS = [
  'q.ECI_ACT_DT',
  'q.VALUE_DT1',
  'q._HSTCLSDATE',
  'q._TRADE_DATE',
] as const;

const ECI_DISPLAY_DATE_FIELDS = [
  'q._TRADE_DATE',
  'q._HSTCLSDATE',
  'q.VALUE_DT1',
  'q.ECI_ACT_DT',
] as const;

const ECI_EQUITY_PRICE_FIELDS = [
  'q._TRDPRC_1',
  'q._OPEN_PRC',
  'q._HIGH_1',
  'q._LOW_1',
  'q._HST_CLOSE',
  'q.OPEN_PRC',
] as const;

const ECI_PRICE_METADATA_TYPE = 'string';

const ECI_MARKET_TREND_FIELDS = [
  'd._PCTCHG_1W',
  'd._PCTCHG_1M',
  'd._PCTCHG_3M',
  'd._PCTCHG_6M',
  'd._PCTCHG_1Y',
  'd._PCTCHG_YR',
  'd._PCTCHG_YTD',
] as const;

const ECI_ZERO_WHEN_BLANK_FIELDS = [
  'q._NETCHNG_1',
  'q._PCTCHNG',
  'q._TURNOVER',
  'q._ACVOL_1',
] as const;

const eciLevelByRic = new Map<string, number>();

const isEciRic = (ric: string): boolean =>
  ric.includes('=ECIX') || ric.endsWith('=ECI') || ric.includes('=ECI,');

const isBlankQuoteValue = (value: string | undefined): boolean => {
  if (value == null || value === '-') {
    return true;
  }
  return value.trim() === '';
};

const parseSignedNumber = (value: string | undefined): number | null => {
  if (isBlankQuoteValue(value)) {
    return null;
  }
  const parsed = Number.parseFloat(String(value).replace(/^\+/, '').trim());
  return Number.isNaN(parsed) ? null : parsed;
};

const ECI_MILLIONS_SCALE_THRESHOLD = 1_000_000;

const toEciMillions = (value: number): number => {
  if (Math.abs(value) >= ECI_MILLIONS_SCALE_THRESHOLD) {
    return value / ECI_MILLIONS_SCALE_THRESHOLD;
  }
  return value;
};

const millionsFromEconField = (value: string | undefined): number | null => {
  const parsed = parseSignedNumber(value);
  if (parsed == null || parsed === 0) {
    return null;
  }
  return toEciMillions(parsed);
};

const formatEciMillionsDisplay = (millions: number): string => {
  if (!Number.isFinite(millions)) {
    return '0 M';
  }
  const sign = millions < 0 ? '-' : '';
  const formatted = Math.abs(millions).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
    useGrouping: false,
  });
  return `${sign}${formatted} M`;
};

const formatEciMillionsForChart = (millions: number): string => {
  if (!Number.isFinite(millions)) {
    return '0';
  }
  return String(millions);
};

const formatYtdMainIndexTradeDate = (): string => {
  const year = new Date().getFullYear();
  return formatMainIndexTradeDate(new Date(year, 0, 1));
};

const formatMainIndexTradeDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = MONTH_LABELS[date.getMonth()];
  return `${day} ${month} ${date.getFullYear()}`;
};

const toMainIndexTradeDate = (
  value: string | undefined,
): string | undefined => {
  if (isBlankQuoteValue(value)) {
    return undefined;
  }
  const trimmed = String(value).trim();
  const parts = trimmed.split(/\s+/);
  if (parts.length >= 3) {
    const numericMonth = Number.parseInt(parts[1], 10);
    if (
      !Number.isNaN(numericMonth) &&
      numericMonth >= 1 &&
      numericMonth <= 12
    ) {
      const month = MONTH_LABELS[numericMonth - 1];
      const day = String(Number.parseInt(parts[0], 10)).padStart(2, '0');
      return `${day} ${month} ${parts[2]}`;
    }
    return trimmed;
  }
  const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!isoMatch) {
    return trimmed;
  }
  const month = MONTH_LABELS[Number.parseInt(isoMatch[2], 10) - 1];
  if (!month) {
    return trimmed;
  }
  const day = String(Number.parseInt(isoMatch[3], 10)).padStart(2, '0');
  return `${day} ${month} ${isoMatch[1]}`;
};

const compactDateMatch = (value: string): RegExpMatchArray | null =>
  value.match(/^(\d{4})(\d{2})(\d{2})$/);

const toMainIndexTradeDateFromCompact = (
  value: string | undefined,
): string | undefined => {
  if (isBlankQuoteValue(value)) {
    return undefined;
  }
  const match = compactDateMatch(String(value).trim());
  if (!match) {
    return undefined;
  }
  const month = MONTH_LABELS[Number.parseInt(match[2], 10) - 1];
  if (!month) {
    return undefined;
  }
  const day = String(Number.parseInt(match[3], 10)).padStart(2, '0');
  return `${day} ${month} ${match[1]}`;
};

const resolveEciReportDate = (row: LsegQuoteRow): string | undefined => {
  for (const field of ECI_REPORT_DATE_FIELDS) {
    const raw = row[field];
    if (isBlankQuoteValue(raw)) {
      continue;
    }
    const fromCompact = toMainIndexTradeDateFromCompact(raw);
    if (fromCompact && isParseableMainIndexTradeDate(fromCompact)) {
      return fromCompact;
    }
    if (isParseableMainIndexTradeDate(raw)) {
      return toMainIndexTradeDate(raw);
    }
  }
  return undefined;
};

const applyEciDisplayDates = (row: LsegQuoteRow): void => {
  const displayDate =
    resolveEciReportDate(row) ?? formatYtdMainIndexTradeDate();
  for (const field of ECI_DISPLAY_DATE_FIELDS) {
    row[field] = displayDate;
  }
};

const isParseableMainIndexTradeDate = (value: string | undefined): boolean => {
  const normalized = toMainIndexTradeDate(value);
  if (!normalized) {
    return false;
  }
  const parts = normalized.split(/\s+/);
  if (parts.length < 3) {
    return false;
  }
  const monthIndex = MONTH_LABELS.findIndex(
    (label) => label.toLowerCase() === parts[1].toLowerCase(),
  );
  if (monthIndex < 0) {
    return false;
  }
  const parsed = new Date(
    Number.parseInt(parts[2], 10),
    monthIndex,
    Number.parseInt(parts[0], 10),
  );
  return !Number.isNaN(parsed.getTime());
};

const eciMillionsFromRow = (row: LsegQuoteRow): number | null => {
  const existing = parseSignedNumber(row['q._TRDPRC_1']);
  if (existing != null && existing !== 0) {
    return toEciMillions(existing);
  }

  const fromPrior = millionsFromEconField(row['q.ECON_PRIOR']);
  if (fromPrior != null) {
    return fromPrior;
  }

  const fromAct = millionsFromEconField(row['q.ECON_ACT']);
  if (fromAct != null) {
    return fromAct;
  }

  const actValNs = parseSignedNumber(row['q.ACT_VAL_NS']);
  if (actValNs != null && Math.abs(actValNs) >= ECI_MILLIONS_SCALE_THRESHOLD) {
    return actValNs / ECI_MILLIONS_SCALE_THRESHOLD;
  }

  const fromCfLast = millionsFromEconField(row['q.CF_LAST']);
  if (fromCfLast != null) {
    return fromCfLast;
  }

  return null;
};

const ensureTrendMetadata = (
  metadata: Record<string, { type?: string }> | undefined,
): Record<string, { type?: string }> => {
  const next = metadata ?? {};
  for (const field of ECI_MARKET_TREND_FIELDS) {
    if (!next[field]) {
      next[field] = { type: 'double' };
    }
  }
  return next;
};

const applyEciMillionsDisplayFields = (
  row: LsegQuoteRow,
  payload: LsegRestPayload,
  millions: number,
): void => {
  const display = formatEciMillionsDisplay(millions);
  const metadata = payload.metadata ?? {};

  for (const field of ECI_EQUITY_PRICE_FIELDS) {
    row[field] = display;
    metadata[field] = { type: ECI_PRICE_METADATA_TYPE };
  }

  payload.metadata = metadata;
};

const normalizeEciQuoteRow = (
  row: LsegQuoteRow,
  ric: string,
  payload: LsegRestPayload,
): void => {
  if (!isEciRic(ric)) {
    return;
  }

  row['q.RIC'] = ric;

  applyEciDisplayDates(row);

  const millions = eciMillionsFromRow(row);
  if (millions != null) {
    eciLevelByRic.set(ric, millions);
    applyEciMillionsDisplayFields(row, payload, millions);
  }

  for (const field of ECI_ZERO_WHEN_BLANK_FIELDS) {
    if (isBlankQuoteValue(row[field])) {
      row[field] = '0';
    }
  }

  payload.metadata = ensureTrendMetadata(payload.metadata);
  for (const field of ECI_MARKET_TREND_FIELDS) {
    if (isBlankQuoteValue(row[field])) {
      row[field] = '0';
    }
  }
};

const pathIncludesEciRic = (pathString: string): boolean => {
  const ricFromRics = pathString.match(/rics=([^&]+)/);
  if (ricFromRics) {
    return decodeURIComponent(ricFromRics[1])
      .split(',')
      .some((ric) => isEciRic(ric.trim()));
  }
  const ricParam = pathString.match(/[?&]ric=([^&]+)/);
  if (ricParam) {
    return isEciRic(decodeURIComponent(ricParam[1]).trim());
  }
  return false;
};

const parseRicFromPath = (pathString: string): string | undefined => {
  const ricFromRics = pathString.match(/rics=([^&]+)/);
  if (ricFromRics) {
    const ric = decodeURIComponent(ricFromRics[1]).split(',')[0]?.trim();
    return ric || undefined;
  }
  const ricParam = pathString.match(/[?&]ric=([^&]+)/);
  if (ricParam) {
    return decodeURIComponent(ricParam[1]).trim();
  }
  return undefined;
};

const parseRicsFromQuoteInfoPath = (pathString: string): string[] => {
  const ricMatch = pathString.match(/rics=([^&]+)/);
  if (!ricMatch) {
    return [];
  }
  return decodeURIComponent(ricMatch[1])
    .split(',')
    .map((ric) => ric.trim())
    .filter(Boolean);
};

const appendEciQuoteFids = (pathString: string): string => {
  if (!pathString.includes('quote/info') || !pathIncludesEciRic(pathString)) {
    return pathString;
  }
  return pathString.replace(/fids=([^&]+)/, (_, fids: string) => {
    const decodedFids = decodeURIComponent(fids);
    const missing = ECI_QUOTE_EXTRA_FIDS.filter(
      (f) => !decodedFids.includes(f),
    );
    if (missing.length === 0) {
      return `fids=${fids}`;
    }
    return `fids=${fids},${missing.join(',')}`;
  });
};

const parseIsoDateFromPath = (
  pathString: string,
  key: string,
): Date | undefined => {
  const match = pathString.match(new RegExp(`${key}=([^&]+)`));
  if (!match) {
    return undefined;
  }
  const parsed = new Date(decodeURIComponent(match[1]));
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const buildSyntheticIntraday = (
  level: number,
  pathString: string,
): LsegTimeseriesRow[] => {
  const end = parseIsoDateFromPath(pathString, 'toDate') ?? new Date();
  const start =
    parseIsoDateFromPath(pathString, 'fromDate') ??
    new Date(
      Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate()),
    );
  const levelString = formatEciMillionsForChart(level);
  const startSec = Math.floor(start.getTime() / 1000);
  const endSec = Math.floor(end.getTime() / 1000);
  return [
    {
      TIME: String(startSec),
      CLOSE: levelString,
      OPEN: levelString,
      HIGH: levelString,
      LOW: levelString,
    },
    {
      TIME: String(endSec),
      CLOSE: levelString,
      OPEN: levelString,
      HIGH: levelString,
      LOW: levelString,
    },
  ];
};

const buildSyntheticHistorical = (
  level: number,
  pathString: string,
): LsegTimeseriesRow[] => {
  const end = parseIsoDateFromPath(pathString, 'toDate') ?? new Date();
  const dateEnd = end.toISOString().slice(0, 10);
  const levelString = formatEciMillionsForChart(level);
  return [
    {
      _DATE_END: `${dateEnd}T00:00:00`,
      CLOSE_PRC: levelString,
      OPEN_PRC: levelString,
      HIGH_1: levelString,
      LOW_1: levelString,
    },
  ];
};

const patchEciTimeseriesResponse = (
  pathString: string,
  response: LsegRestGetResponse,
): LsegRestGetResponse => {
  if (
    !pathIncludesEciRic(pathString) ||
    (!pathString.includes('/timeseries/intraday') &&
      !pathString.includes('/timeseries/historical'))
  ) {
    return response;
  }

  const payload = response.data;
  if (!payload) {
    return response;
  }

  const rows = payload.data;
  if (Array.isArray(rows) && rows.length > 0) {
    return response;
  }

  const ric = parseRicFromPath(pathString);
  if (!ric) {
    return response;
  }

  const level = eciLevelByRic.get(ric);
  if (level == null) {
    return response;
  }

  const synthetic = pathString.includes('/timeseries/intraday')
    ? buildSyntheticIntraday(level, pathString)
    : buildSyntheticHistorical(level, pathString);

  payload.data = synthetic;
  payload.substatus =
    payload.substatus === 'NotReady' ? 'OK' : (payload.substatus ?? 'OK');

  return response;
};

export const applyLsegMainIndexEciSupport = (doryApi: LsegDoryApi): void => {
  const rest = doryApi.Rest;
  if (!rest?.get || rest.__ovMainIndexEciSupportApplied) {
    return;
  }

  const originalGet = rest.get.bind(rest);

  rest.get = async (path, ...restArgs) => {
    const pathString = typeof path === 'string' ? path : String(path ?? '');

    if (!pathIncludesEciRic(pathString)) {
      return originalGet(path, ...restArgs);
    }

    const requestPath = appendEciQuoteFids(pathString);
    const response = await originalGet(requestPath, ...restArgs);

    if (requestPath.includes('quote/info')) {
      const payload = response.data;
      const rows = payload?.data;
      if (Array.isArray(rows) && payload) {
        const rics = parseRicsFromQuoteInfoPath(requestPath);
        rows.forEach((row, index) => {
          if (typeof row !== 'object' || row == null) {
            return;
          }
          const quoteRow = row as LsegQuoteRow;
          const ric = quoteRow['q.RIC'] ?? rics[index] ?? rics[0];
          if (ric && isEciRic(ric)) {
            normalizeEciQuoteRow(quoteRow, ric, payload);
          }
        });
      }
    }

    return patchEciTimeseriesResponse(requestPath, response);
  };

  rest.__ovMainIndexEciSupportApplied = true;
};
